import { useState } from 'react';
import {
  Shield,
  Plus,
  Trash2,
  Loader2,
  Globe,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminLayout } from '@/components/admin';
import { Card, Button, Badge } from '@/components/ui';
import { useAdminWhitelist } from '@/hooks/useAdminWhitelist';

const AdminWhitelist = () => {
  const { t } = useTranslation();
  const { domains, loading, error, add, remove } = useAdminWhitelist();
  const [newDomain, setNewDomain] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setActionLoading('add');
    try {
      await add(newDomain);
      setNewDomain('');
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (domain: string) => {
    setActionLoading(domain);
    try {
      await remove(domain);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
          {t('admin.whitelistTitle')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {t('admin.whitelistSubtitle')}
        </p>
      </div>

      {/* Add domain form */}
      <Card variant="default" padding="md" className="mb-6">
        <form onSubmit={handleAdd} className="flex gap-3">
          <div className="relative flex-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder={t('admin.addDomainPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:text-white dark:placeholder-gray-400"
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={actionLoading === 'add' || !newDomain.trim()}
          >
            {actionLoading === 'add' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                {t('admin.addDomain')}
              </>
            )}
          </Button>
        </form>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Domain list */}
      <Card variant="default" padding="none">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        ) : domains.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">{t('admin.noDomains')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {domains.map((domain) => (
              <div
                key={domain}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/40 rounded-xl flex items-center justify-center">
                    <Globe className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="font-medium text-dark-800 dark:text-white">{domain}</span>
                  <Badge variant="success" size="sm">
                    {t('admin.whitelisted')}
                  </Badge>
                </div>
                <button
                  onClick={() => handleRemove(domain)}
                  disabled={actionLoading === domain}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                >
                  {actionLoading === domain ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </AdminLayout>
  );
};

export default AdminWhitelist;
